import { useCallback, useMemo, useRef, useState } from 'react';

import { Table } from '@tanstack/react-table';

import { DEFAULT_PAGE_SIZE } from '@/lib/constants/pagination';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { revealTableRow } from '@/modules/finance/common/utils/reveal-table-row';
import { getShortProjectsRaw } from '@/modules/projects';
import { ShortProjectResponse } from '@/modules/projects/types/dto/response/short-project-response';

import { getInvoiceProjectRowId } from '../components/generate-invoices-table/invoice-row.helpers';
import { mapShortProjectToCustomInvoiceRow } from '../mappers';
import { GenerateInvoiceListItem } from '../types';

export interface InvoiceCustomProjects {
  customProjects: GenerateInvoiceListItem[];
  addCustomProject: (project: ShortProjectResponse) => void;
  removeCustomProject: (projectId: string) => void;
  hasCustomProjects: boolean;
  reset: () => void;
  /**
   * Selects, expands and scrolls to rows added since the last render. Call from an effect keyed
   * on the composed table data — a new row is not in TanStack's model until that data rebuilds.
   */
  flushPendingRows: (table: Table<GenerateInvoiceListItem>) => void;
  customProjectIds: Set<number>;
  fetchProjects: (
    search: string,
    cursor: number | null
  ) => Promise<PaginatedResponse<ShortProjectResponse>>;
}

export const useInvoiceCustomProjects = (): InvoiceCustomProjects => {
  const [customProjects, setCustomProjects] = useState<GenerateInvoiceListItem[]>([]);
  const pendingSelections = useRef<string[]>([]);

  const customProjectIds = useMemo(
    () => new Set(customProjects.map(p => p.project.id)),
    [customProjects]
  );

  const addCustomProject = useCallback((project: ShortProjectResponse) => {
    pendingSelections.current.push(getInvoiceProjectRowId(project.id));
    setCustomProjects(prev => [...prev, mapShortProjectToCustomInvoiceRow(project)]);
  }, []);

  const removeCustomProject = useCallback((projectId: string) => {
    setCustomProjects(prev => prev.filter(p => p.id !== projectId));
  }, []);

  const flushPendingRows = useCallback((table: Table<GenerateInvoiceListItem>) => {
    const rowIds = pendingSelections.current;
    if (rowIds.length === 0) return;
    pendingSelections.current = [];
    table.setRowSelection(prev => ({
      ...prev,
      ...Object.fromEntries(rowIds.map(id => [id, true])),
    }));
    // The row lands at the bottom of the table, usually below the fold: expand it and bring it
    // into view so the user sees what they just added.
    revealTableRow(table, rowIds[rowIds.length - 1]);
  }, []);

  const reset = useCallback(() => {
    setCustomProjects([]);
  }, []);

  const fetchProjects = useCallback(
    (search: string, cursor: number | null): Promise<PaginatedResponse<ShortProjectResponse>> =>
      getShortProjectsRaw({
        filter: search ? { searchString: search } : undefined,
        pagination: { cursor, pageSize: DEFAULT_PAGE_SIZE },
      }),
    []
  );

  return {
    customProjects,
    addCustomProject,
    removeCustomProject,
    hasCustomProjects: customProjects.length > 0,
    reset,
    flushPendingRows,
    customProjectIds,
    fetchProjects,
  };
};
